'use client';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Chapter, TestResultStatisticsSection } from '@/types';
import { useRoute } from '@/utils/client/route';
import {
  getFocusSections,
  getPerformanceColor,
  getSectionPercentage,
  resolveExerciseSlug,
} from '@/utils/client/test-feedback';

export default function TestNextSteps({
  sections,
  curriculum,
  courseSlug,
  fallbackSlug,
}: {
  sections: TestResultStatisticsSection[];
  curriculum: Chapter[];
  courseSlug: string;
  fallbackSlug?: string;
}) {
  const { t } = useTranslation();
  const { goToRoute } = useRoute();

  const focusSections = getFocusSections(sections);

  if (!focusSections.length) return null;

  const navigateToSection = (section: TestResultStatisticsSection) => {
    const slug = resolveExerciseSlug(curriculum, section.exercises) ?? fallbackSlug;
    if (!slug) return;

    goToRoute('myCourseLearn', {
      courseSlug,
      chapterSlug: slug,
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mb: 0.375 }}>
        {t('test.insight.nextSteps.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('test.insight.nextSteps.subtitle')}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: `repeat(${focusSections.length}, 1fr)` },
          gap: 2,
        }}>
        {focusSections.map((section, index) => {
          const slug = resolveExerciseSlug(curriculum, section.exercises) ?? fallbackSlug;
          const percentage = getSectionPercentage(section);
          const color = getPerformanceColor(percentage);

          return (
            <Box
              key={section._id}
              sx={{
                p: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                borderLeft: 4,
                borderLeftColor: color,
                bgcolor: `${color}0d`,
              }}>
              <Box sx={{ mb: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                  }}>
                  <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                    {t('test.insight.nextSteps.priority', { rank: index + 1 })}
                  </Typography>
                  <Typography
                    variant="h5"
                    component="span"
                    sx={{ fontWeight: 700, color, flexShrink: 0 }}>
                    {percentage}%
                  </Typography>
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {section.label}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={percentage}
                sx={{
                  height: 6,
                  borderRadius: 1,
                  mb: 1.5,
                  bgcolor: 'action.hover',
                  '& .MuiLinearProgress-bar': { bgcolor: color },
                }}
              />
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t('test.insight.nextSteps.strategy')}
              </Typography>
              {slug && (
                <Button size="small" variant="outlined" onClick={() => navigateToSection(section)}>
                  {t('test.insight.nextSteps.open')}
                </Button>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
